import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/auth.service';
import type { RegraTabelaPreco, RestricaoPagto } from './usePoliticasGlobais';

export interface PoliticasVendedor {
  vendedor_id: number;
  pode_selecionar_tabela: boolean | null;
  tabela_preco_padrao_id: number | null;
  pode_dar_desconto_item: boolean | null;
  limite_desconto_item: number | null;
  pode_dar_desconto_total: boolean | null;
  limite_desconto_total: number | null;
  origem_pedido_padrao_id: number | null;
  // AIDEV-NOTE: null = herda o ponto de estoque das politicas globais
  ponto_estoque_id: number | null;
  origem_padrao_id: number | null;
  pode_alterar_origem_pedido: boolean | null;
  empresa_padrao_id: number | null;
  pode_alterar_empresa: boolean | null;
  pode_cadastrar_clientes: boolean | null;
  config_sync_pedido: 'automatico' | 'manual' | null;
  tipo_comercializacao_padrao_id: number | null;
  tipo_movimento_receber_padrao_id: number | null;
  pode_alterar_preco_unitario: boolean | null;
  limite_reducao_preco_pct: number | null;
  limite_aumento_preco_pct: number | null;
}

export interface RestricaoProduto {
  id: string;
  tipo: 'grupo' | 'subgrupo' | 'produto';
  codigo: number | null;
  grupo: string | null;
  subgrupo: string | null;
}

// AIDEV-NOTE: Hook para políticas específicas de vendedor (override sobre políticas globais)
export function usePoliticasVendedor(vendedorId: number) {
  const qc = useQueryClient();

  const politicasQuery = useQuery({
    queryKey: ['admin-politicas-vendedor', vendedorId],
    enabled: !!vendedorId,
    queryFn: async () => {
      const res = await api.get<{ global: any; override: PoliticasVendedor | null }>(
        `/admin/vendedores/${vendedorId}/politicas`
      );
      return res.data;
    }
  });

  const regrasQuery = useQuery({
    queryKey: ['admin-regras-vendedor', vendedorId],
    enabled: !!vendedorId,
    queryFn: async (): Promise<RegraTabelaPreco[]> => {
      const res = await api.get<{ regras: RegraTabelaPreco[] }>(
        `/admin/vendedores/${vendedorId}/tabelas-preco/regras`
      );
      return res.data.regras;
    }
  });

  const restricoesPagtoQuery = useQuery({
    queryKey: ['admin-restricoes-pagto-vendedor', vendedorId],
    enabled: !!vendedorId,
    queryFn: async (): Promise<RestricaoPagto[]> => {
      const res = await api.get<{ restricoes: RestricaoPagto[] }>(
        `/admin/vendedores/${vendedorId}/condicoes-pagto`
      );
      return res.data.restricoes;
    }
  });

  const restricoesProdutosQuery = useQuery({
    queryKey: ['admin-restricoes-produtos-vendedor', vendedorId],
    enabled: !!vendedorId,
    queryFn: async (): Promise<RestricaoProduto[]> => {
      const res = await api.get<{ restricoes: RestricaoProduto[] }>(
        `/admin/vendedores/${vendedorId}/restricoes-produtos`
      );
      return res.data.restricoes;
    }
  });

  const savePoliticasMutation = useMutation({
    mutationFn: async (payload: Partial<PoliticasVendedor>) => {
      await api.put(`/admin/vendedores/${vendedorId}/politicas`, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-politicas-vendedor', vendedorId] })
  });

  const saveRegrasMutation = useMutation({
    mutationFn: async (regras: Omit<RegraTabelaPreco, 'id'>[]) => {
      await api.put(`/admin/vendedores/${vendedorId}/tabelas-preco/regras`, { regras });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-regras-vendedor', vendedorId] })
  });

  const saveRestricoesPagtoMutation = useMutation({
    mutationFn: async (restricoes: Omit<RestricaoPagto, 'id'>[]) => {
      await api.put(`/admin/vendedores/${vendedorId}/condicoes-pagto`, { restricoes });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin-restricoes-pagto-vendedor', vendedorId] })
  });

  const addRestricaoProdutoMutation = useMutation({
    mutationFn: async (payload: {
      tipo: 'grupo' | 'subgrupo' | 'produto';
      codigo?: number;
      grupo?: string;
      subgrupo?: string;
    }) => {
      await api.post(`/admin/vendedores/${vendedorId}/restricoes-produtos`, payload);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin-restricoes-produtos-vendedor', vendedorId] })
  });

  const removeRestricaoProdutoMutation = useMutation({
    mutationFn: async (restricaoId: string) => {
      await api.delete(`/admin/vendedores/${vendedorId}/restricoes-produtos/${restricaoId}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin-restricoes-produtos-vendedor', vendedorId] })
  });

  return {
    politicasQuery,
    regrasQuery,
    restricoesPagtoQuery,
    restricoesProdutosQuery,
    savePoliticasMutation,
    saveRegrasMutation,
    saveRestricoesPagtoMutation,
    addRestricaoProdutoMutation,
    removeRestricaoProdutoMutation
  };
}
